// Logistics hooks (R-074). The driver's POD and temperature checkpoints
// queue OFFLINE like workforce completions (Lastenheft 7.10): a network
// failure lands them in a localStorage queue keyed by a client-generated
// UUID and they flush on reconnect — the server upserts PODs by that
// `client_ref`, so replays are no-ops. The driver's tour queries persist to
// localStorage so the view survives dead spots on the road.
import { useEffect, useState } from 'preact/hooks';
import { newClientRef } from '../../core/client-ref';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../core/client';
import type {
  CompleteTourResult,
  DeliveryOrder,
  DoDetail,
  PackageRecord,
  PackageResolution,
  PodRecord,
  Tour,
  TourDetail,
  TrackingResult,
} from './logistics.types';

const LG = '/users/v1/logistics';
const KEY = ['logistics'] as const;
const CACHE_KEY = 'xantory:lg:tours';
const QUEUE_KEY = 'xantory:lg:queue';

// ── offline queue (POD + checkpoints; 7.10 idempotency) ─────────────────────

export interface QueuedDelivery {
  /** The idempotency key — generated ONCE when the POD is captured. */
  client_ref: string;
  kind: 'pod' | 'checkpoint';
  /** POD: the delivery order id · checkpoint: the tour id. */
  target_id: string;
  signature_data?: string;
  receiver_name?: string;
  note?: string;
  lat?: number;
  lng?: number;
  temp_c?: number;
  queued_at: number;
}

function readQueue(): QueuedDelivery[] {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]') as QueuedDelivery[];
  } catch {
    return [];
  }
}

function writeQueue(q: QueuedDelivery[]) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  window.dispatchEvent(new Event('lg-queue-changed'));
}

/** Queueable failure = the request never reached the authoritative server
 *  (fetch-level error or the proxy's 502/503/504). Real 4xx answers are
 *  server decisions and are never queued. */
function isNetworkError(e: unknown): boolean {
  if (!(e instanceof ApiError)) return true;
  return e.status === 502 || e.status === 503 || e.status === 504;
}

async function sendQueued(a: QueuedDelivery): Promise<void> {
  if (a.kind === 'pod') {
    await api.post<PodRecord>(`${LG}/orders/${a.target_id}/pod`, {
      client_ref: a.client_ref,
      signature_data: a.signature_data ?? '',
      receiver_name: a.receiver_name ?? '',
      ...(a.note && { note: a.note }),
      ...(a.lat != null && { lat: a.lat }),
      ...(a.lng != null && { lng: a.lng }),
    });
  } else {
    await api.post<TrackingResult>(`${LG}/tours/${a.target_id}/tracking`, {
      source: 'driver',
      ...(a.temp_c != null && { temp_c: a.temp_c }),
      ...(a.lat != null && { lat: a.lat }),
      ...(a.lng != null && { lng: a.lng }),
      ...(a.note && { note: a.note }),
    });
  }
}

/** Flush front-to-back; stop at the first network failure. Server-rejected
 *  entries (4xx) are dropped — the server is authoritative. */
export async function flushLogisticsQueue(): Promise<number> {
  let queue = readQueue();
  let flushed = 0;
  while (queue.length > 0) {
    const head = queue[0]!;
    try {
      await sendQueued(head);
      flushed += 1;
    } catch (e) {
      if (isNetworkError(e)) break;
    }
    queue = queue.slice(1);
    writeQueue(queue);
  }
  return flushed;
}

/** Pending queue — drives the driver view's sync badge. */
export function useLogisticsQueue(): QueuedDelivery[] {
  const [queue, setQueue] = useState<QueuedDelivery[]>(readQueue);
  useEffect(() => {
    const update = () => setQueue(readQueue());
    window.addEventListener('lg-queue-changed', update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener('lg-queue-changed', update);
      window.removeEventListener('storage', update);
    };
  }, []);
  return queue;
}

/** Reconnect → flush → refetch. Mount once on the driver view. */
export function useLogisticsOfflineSync() {
  const qc = useQueryClient();
  useEffect(() => {
    const flush = () => {
      if (readQueue().length === 0) return;
      void flushLogisticsQueue().then((n) => {
        if (n > 0) void qc.invalidateQueries({ queryKey: KEY });
      });
    };
    window.addEventListener('online', flush);
    const retry = window.setInterval(flush, 30_000);
    flush();
    return () => {
      window.removeEventListener('online', flush);
      window.clearInterval(retry);
    };
  }, [qc]);
}

// ── delivery orders ──────────────────────────────────────────────────────────

/** A refresh somebody asked for on the Logistics screen: refetches every ACTIVE logistics read
 *  (delivery orders, tours, a tour's detail). They poll every 30 s; this is the "now". */
export function useRefreshLogistics() {
  const qc = useQueryClient();
  return () => qc.refetchQueries({ queryKey: KEY, type: 'active' });
}

export function useDeliveryOrders(status: string) {
  return useQuery({
    queryKey: [...KEY, 'orders', status],
    queryFn: () => api.get<DeliveryOrder[]>(`${LG}/orders?status=${status}`),
    refetchInterval: 30_000,
    // A filter change keeps the current rows (flagged `isPlaceholderData`) while
    // the new status loads, so the list dims instead of vanishing.
    placeholderData: keepPreviousData,
  });
}

export function useDeliveryOrder(id: string | null) {
  return useQuery({
    queryKey: [...KEY, 'order', id],
    queryFn: () => api.get<DoDetail>(`${LG}/orders/${id}`),
    enabled: !!id,
  });
}

export function useCreateDeliveryOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: {
      site_id: string;
      customer_name: string;
      customer_contact?: string;
      delivery_address: string;
      window_from: number;
      window_to: number;
      order_ref?: string;
      note?: string;
      items: { batch_id: string; ordered_qty_kg: number }[];
    }) => api.post<DeliveryOrder>(`${LG}/orders`, r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useMarkReady() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<DeliveryOrder>(`${LG}/orders/${id}/ready`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<boolean>(`${LG}/orders/${id}/cancel`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function usePackItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      item_id,
      actual_weight_kg,
      cold_chain_note,
    }: {
      item_id: string;
      actual_weight_kg: number;
      cold_chain_note?: string;
    }) =>
      api.post<PackageRecord>(`${LG}/items/${item_id}/pack`, {
        actual_weight_kg,
        ...(cold_chain_note && { cold_chain_note }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useResolvePackage() {
  return useMutation({
    mutationFn: (code: string) => api.get<PackageResolution>(`${LG}/packages/${encodeURIComponent(code)}`),
  });
}

// ── tours (driver queries persist for offline reads) ────────────────────────

function readTourCache(status: string, scope: string): Tour[] | undefined {
  try {
    const cached = JSON.parse(localStorage.getItem(`${CACHE_KEY}:${status}:${scope}`) ?? 'null');
    return (cached as Tour[]) ?? undefined;
  } catch {
    return undefined;
  }
}

export function useTours(status: string, scope: '' | 'mine') {
  const query = useQuery({
    queryKey: [...KEY, 'tours', status, scope],
    queryFn: () => api.get<Tour[]>(`${LG}/tours?status=${status}&scope=${scope}`),
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });
  useEffect(() => {
    if (query.data) {
      localStorage.setItem(`${CACHE_KEY}:${status}:${scope}`, JSON.stringify(query.data));
    }
  }, [query.data, status, scope]);
  return { ...query, data: query.data ?? readTourCache(status, scope) };
}

function readTourDetailCache(id: string | null): TourDetail | undefined {
  try {
    const cached = JSON.parse(localStorage.getItem(`${CACHE_KEY}:detail:${id}`) ?? 'null');
    return (cached as TourDetail) ?? undefined;
  } catch {
    return undefined;
  }
}

export function useTour(id: string | null) {
  const query = useQuery({
    queryKey: [...KEY, 'tour', id],
    queryFn: () => api.get<TourDetail>(`${LG}/tours/${id}`),
    enabled: !!id,
    refetchInterval: 30_000,
  });
  useEffect(() => {
    if (query.data && id) {
      localStorage.setItem(`${CACHE_KEY}:detail:${id}`, JSON.stringify(query.data));
    }
  }, [query.data, id]);
  return { ...query, data: query.data ?? readTourDetailCache(id) };
}

export function useCreateTour() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: {
      site_id: string;
      tour_date: string;
      driver_id: string;
      vehicle: string;
      vehicle_asset_id?: string;
      temp_min_c?: number;
      temp_max_c?: number;
    }) => api.post<Tour>(`${LG}/tours`, r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAssignStop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tour_id, delivery_order_id }: { tour_id: string; delivery_order_id: string }) =>
      api.post<DeliveryOrder>(`${LG}/tours/${tour_id}/stops`, { delivery_order_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUnassignStop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (delivery_order_id: string) =>
      api.post<boolean>(`${LG}/orders/${delivery_order_id}/unassign`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSequenceStops() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tour_id, delivery_order_ids }: { tour_id: string; delivery_order_ids: string[] }) =>
      api.put<boolean>(`${LG}/tours/${tour_id}/sequence`, { delivery_order_ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useStartLoading() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Tour>(`${LG}/tours/${id}/loading`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Scan-confirm a package onto the truck; a foreign code is a 409. */
export function useLoadPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tour_id, package_code }: { tour_id: string; package_code: string }) =>
      api.post<PackageRecord>(`${LG}/tours/${tour_id}/load`, { package_code }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useStartTour() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Tour>(`${LG}/tours/${id}/start`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCompleteTour() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<CompleteTourResult>(`${LG}/tours/${id}/complete`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCancelTour() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<boolean>(`${LG}/tours/${id}/cancel`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// ── tracking & POD (offline-queueable) ───────────────────────────────────────

/** Fire a driver position ping (best effort — never queued: pings are
 *  periodic, a lost one is superseded by the next). */
export async function sendPing(tour_id: string, lat: number, lng: number): Promise<void> {
  try {
    await api.post<TrackingResult>(`${LG}/tours/${tour_id}/tracking`, { source: 'driver', lat, lng });
  } catch {
    // offline — the next ping covers it
  }
}

/** Temperature checkpoint with offline queueing. */
export function useRecordCheckpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tour_id,
      temp_c,
      note,
      lat,
      lng,
    }: {
      tour_id: string;
      temp_c: number;
      note?: string;
      lat?: number;
      lng?: number;
    }) => {
      try {
        return await api.post<TrackingResult>(`${LG}/tours/${tour_id}/tracking`, {
          source: 'driver',
          temp_c,
          ...(note && { note }),
          ...(lat != null && { lat }),
          ...(lng != null && { lng }),
        });
      } catch (e) {
        if (!isNetworkError(e)) throw e;
        writeQueue([
          ...readQueue(),
          {
            client_ref: newClientRef(),
            kind: 'checkpoint',
            target_id: tour_id,
            temp_c,
            ...(note && { note }),
            ...(lat != null && { lat }),
            ...(lng != null && { lng }),
            queued_at: Date.now(),
          },
        ]);
        return null;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** POD with offline queueing; the client_ref is generated HERE, once, so an
 *  offline replay after reconnect is idempotent server-side (7.10). */
export function useSubmitPod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      delivery_order_id,
      signature_data,
      receiver_name,
      note,
      lat,
      lng,
    }: {
      delivery_order_id: string;
      signature_data: string;
      receiver_name: string;
      note?: string;
      lat?: number;
      lng?: number;
    }) => {
      const client_ref = newClientRef();
      try {
        return await api.post<PodRecord>(`${LG}/orders/${delivery_order_id}/pod`, {
          client_ref,
          signature_data,
          receiver_name,
          ...(note && { note }),
          ...(lat != null && { lat }),
          ...(lng != null && { lng }),
        });
      } catch (e) {
        if (!isNetworkError(e)) throw e;
        writeQueue([
          ...readQueue(),
          {
            client_ref,
            kind: 'pod',
            target_id: delivery_order_id,
            signature_data,
            receiver_name,
            ...(note && { note }),
            ...(lat != null && { lat }),
            ...(lng != null && { lng }),
            queued_at: Date.now(),
          },
        ]);
        return null;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
